import { ArrowRight, Clock, FileText, User } from "lucide-react";
import { useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import {
  formatDateWIB,
  formatEntityName,
  formatValueForDisplay,
  getActionMeta,
} from "./activity-log-utils";

import type { ActivityLog } from "@artisancode/api-types";

export function ActivityLogDetailDialog({
  open,
  onOpenChange,
  log,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  log: ActivityLog | null;
}) {
  const [tab, setTab] = useState<"diff" | "before" | "after">("diff");

  if (!log) return null;

  const actionMeta = getActionMeta(log.activity);
  const diffEntries = log.diff ? Object.entries(log.diff) : [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[85vh] flex flex-col p-6">
        <DialogHeader className="space-y-3 pb-3 border-b border-border">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <DialogTitle className="text-lg font-semibold">
                Detail Aktivitas
              </DialogTitle>
              <Badge variant="outline" className={actionMeta.badgeClass}>
                {actionMeta.label}
              </Badge>
              <Badge variant="secondary">
                {formatEntityName(log.entityName)}
              </Badge>
            </div>
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Clock className="w-3.5 h-3.5" />
              <span>{formatDateWIB(log.createdAt)}</span>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-y-1 gap-x-4 text-xs text-muted-foreground bg-muted/40 p-2.5 rounded-lg border border-border/50">
            <div className="flex items-center gap-1.5">
              <User className="w-3.5 h-3.5 text-primary" />
              <span className="font-medium text-foreground">
                {log.userName || "System"}
              </span>
              {log.userEmail && (
                <span className="text-muted-foreground">({log.userEmail})</span>
              )}
            </div>
            <div className="flex items-center gap-1.5">
              <FileText className="w-3.5 h-3.5 text-muted-foreground" />
              <span>ID Entitas:</span>
              <code className="bg-background px-1.5 py-0.5 rounded text-[11px] font-mono border">
                {log.entityId}
              </code>
            </div>
          </div>
        </DialogHeader>

        {/* Tab Selection */}
        <div className="flex items-center gap-2 pt-2">
          <Button
            type="button"
            size="sm"
            variant={tab === "diff" ? "default" : "outline"}
            onClick={() => setTab("diff")}
            className="h-8 text-xs"
          >
            Perubahan Kolom ({diffEntries.length})
          </Button>
          <Button
            type="button"
            size="sm"
            variant={tab === "before" ? "default" : "outline"}
            onClick={() => setTab("before")}
            className="h-8 text-xs"
          >
            Data Sebelum
          </Button>
          <Button
            type="button"
            size="sm"
            variant={tab === "after" ? "default" : "outline"}
            onClick={() => setTab("after")}
            className="h-8 text-xs"
          >
            Data Sesudah
          </Button>
        </div>

        {/* Content Body */}
        <div className="flex-1 overflow-y-auto mt-2 pr-1">
          {tab === "diff" && (
            <div className="space-y-3">
              {diffEntries.length > 0 ? (
                <div className="rounded-md border overflow-hidden">
                  <Table>
                    <TableHeader className="bg-muted/50">
                      <TableRow>
                        <TableHead className="w-1/4">Kolom</TableHead>
                        <TableHead className="w-[37.5%]">
                          Nilai Sebelum
                        </TableHead>
                        <TableHead className="w-[37.5%]">
                          Nilai Sesudah
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {diffEntries.map(([field, delta]) => (
                        <TableRow key={field}>
                          <TableCell className="font-mono text-xs font-semibold text-foreground align-top">
                            {field}
                          </TableCell>
                          <TableCell className="text-xs align-top bg-rose-500/5 text-rose-800 dark:text-rose-300">
                            <pre className="whitespace-pre-wrap font-sans text-xs">
                              {formatValueForDisplay(delta.before)}
                            </pre>
                          </TableCell>
                          <TableCell className="text-xs align-top bg-emerald-500/5 text-emerald-800 dark:text-emerald-300">
                            <div className="flex items-start gap-1">
                              <ArrowRight className="w-3 h-3 mt-0.5 text-emerald-600 dark:text-emerald-400 shrink-0" />
                              <pre className="whitespace-pre-wrap font-sans text-xs">
                                {formatValueForDisplay(delta.after)}
                              </pre>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : log.activity === "create" ? (
                <div className="p-8 text-center text-sm text-muted-foreground border rounded-lg bg-muted/20">
                  Data baru dibuat. Buka tab <strong>Data Sesudah</strong> untuk
                  melihat snapshot lengkap.
                </div>
              ) : log.activity === "delete" ? (
                <div className="p-8 text-center text-sm text-muted-foreground border rounded-lg bg-muted/20">
                  Data dihapus. Buka tab <strong>Data Sebelum</strong> untuk
                  melihat snapshot data sebelum penghapusan.
                </div>
              ) : (
                <div className="p-8 text-center text-sm text-muted-foreground border rounded-lg bg-muted/20">
                  Tidak ada perbedaan kolom yang terdeteksi.
                </div>
              )}
            </div>
          )}

          {tab === "before" && (
            <div className="rounded-lg border bg-muted/30 p-4 overflow-x-auto">
              {log.before ? (
                <pre className="font-mono text-xs text-foreground leading-relaxed">
                  {JSON.stringify(log.before, null, 2)}
                </pre>
              ) : (
                <p className="text-sm text-muted-foreground italic">
                  Tidak ada data sebelum perubahan (data dibuat baru).
                </p>
              )}
            </div>
          )}

          {tab === "after" && (
            <div className="rounded-lg border bg-muted/30 p-4 overflow-x-auto">
              {log.after ? (
                <pre className="font-mono text-xs text-foreground leading-relaxed">
                  {JSON.stringify(log.after, null, 2)}
                </pre>
              ) : (
                <p className="text-sm text-muted-foreground italic">
                  Tidak ada data sesudah perubahan (data dihapus).
                </p>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
